import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Button, Card, Cluster, Input, Stack, useToast } from "@salesapp/ui";
import { reportSaveBlocked, reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import { crmApi } from "../../lib/crmApi";
import { formatDateTimeValue, parseApiInstant, zonedToInstant, zonedWallClock } from "../../lib/localeFormat";
import { mailboxApi, type MeetingItem, type Schedule } from "../../lib/mailboxApi";
import { opsApi } from "../../lib/opsApi";
import { useAccess } from "../../shell/AccessProvider";
import { useAuth } from "../../shell/AuthProvider";
import { useLocale } from "../../shell/LocaleProvider";
import { CompanyLookup } from "./CompanyLookup";
import { DeleteRecordButton, ListWorkspace } from "./RecordWorkspace";

// A calendar day is a UTC-midnight Date standing for that day in the organisation's
// timezone, so the grid never depends on the browser's own clock zone.
function dayOf(instant: Date, zone: string): Date {
  const wall = zonedWallClock(instant, zone);
  return new Date(Date.UTC(wall.year, wall.month - 1, wall.day));
}

function addDays(day: Date, days: number): Date {
  const copy = new Date(day);
  copy.setUTCDate(copy.getUTCDate() + days);
  return copy;
}

function startOfWeek(day: Date): Date {
  const weekday = day.getUTCDay();
  return addDays(day, weekday === 0 ? -6 : 1 - weekday);
}

function startOfMonth(day: Date): Date {
  return new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), 1));
}

function addMonths(day: Date, months: number): Date {
  return new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth() + months, 1));
}

function dayStart(day: Date, zone: string): Date {
  return zonedToInstant(
    { year: day.getUTCFullYear(), month: day.getUTCMonth() + 1, day: day.getUTCDate(), hour: 0, minute: 0 },
    zone,
  );
}

function dayLabel(day: Date, options: Intl.DateTimeFormatOptions): string {
  return day.toLocaleDateString(undefined, { ...options, timeZone: "UTC" });
}

function toInput(instant: Date, zone: string): string {
  const wall = zonedWallClock(instant, zone);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`;
}

function fromInput(value: string, zone: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number);
  return zonedToInstant({ year, month, day, hour, minute }, zone);
}

const HOUR = 60 * 60 * 1000;

const STAGE_TONES = new Set([
  "new",
  "working",
  "pre_qualified",
  "qualified",
  "disqualified",
  "lead_in",
  "qualifying",
  "proposal",
  "negotiation",
  "closed_won",
  "closed_lost",
]);

function stageClass(stage: string): string {
  if (STAGE_TONES.has(stage)) return `crm-badge sales-cal-badge sales-cal-badge--${stage}`;
  return "crm-badge crm-badge--draft";
}

function eventClass(stage: string): string {
  if (STAGE_TONES.has(stage)) return `sales-cal-event sales-cal-event--${stage}`;
  return "sales-cal-event";
}

/** Events live in the calendar; meetings and follow-ups are activities. */
function capabilityFor(item: MeetingItem): string {
  return item.source === "event" ? "crm.calendar" : "crm.activities";
}

type CalendarView = "week" | "month";

export function SalesCalendarPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { eventId } = useParams();
  const { orgTimezone, locale, dateFormat, timeFormat } = useLocale();
  const zone = orgTimezone || "UTC";
  const { session } = useAuth();
  const { can } = useAccess();
  const [view, setView] = useState<CalendarView>("week");
  // null follows today in the organisation's timezone, which is only known once the locale loads.
  const [cursor, setCursor] = useState<Date | null>(null);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [selected, setSelected] = useState<MeetingItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MeetingItem | null>(null);
  const [title, setTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [starts, setStarts] = useState("");
  const [ends, setEnds] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const formatWhen = useCallback(
    (iso: string) => formatDateTimeValue(iso, { timezone: zone, locale, dateFormat, timeFormat }),
    [zone, locale, dateFormat, timeFormat],
  );
  const today = dayOf(new Date(), zone);
  const anchor = cursor ?? today;
  const weekStart = startOfWeek(anchor);
  const monthStart = startOfMonth(anchor);
  const rangeStart = view === "week" ? weekStart : startOfWeek(monthStart);
  const dayCount = view === "week" ? 7 : 42;
  const days = Array.from({ length: dayCount }, (_, index) => addDays(rangeStart, index));
  const weekdays = days.slice(0, 7);
  const meetings = useMemo(
    () => [...(schedule?.meetings ?? []), ...(schedule?.events ?? [])],
    [schedule],
  );

  const startIso = dayStart(rangeStart, zone).toISOString();
  const endIso = dayStart(addDays(rangeStart, dayCount), zone).toISOString();
  const load = useCallback(async () => {
    const row = await mailboxApi.schedule(orgId, startIso, endIso);
    setSchedule(row);
    setUpdatedAt(new Date().toISOString());
    setSelected((current) => {
      if (!current) return current;
      const next = [...row.meetings, ...row.events, ...row.follow_ups].find(
        (item) => item.id === current.id && item.source === current.source,
      );
      return next ?? current;
    });
  }, [orgId, startIso, endIso]);

  useEffect(() => {
    let cancelled = false;
    load().catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : "The calendar could not be loaded.");
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    if (!eventId) return;
    const hit = meetings.find((item) => item.id === eventId);
    if (hit) setSelected(hit);
  }, [eventId, meetings]);

  function openCreate() {
    const wall = zonedWallClock(new Date(), zone);
    const start = zonedToInstant({ ...wall, hour: wall.hour + 1, minute: 0 }, zone);
    setEditing(null);
    setTitle("");
    setCompanyName("");
    setCompanyId("");
    setStarts(toInput(start, zone));
    setEnds(toInput(new Date(start.getTime() + HOUR), zone));
    setFormOpen(true);
    setError(null);
  }

  function openEdit(item: MeetingItem) {
    const start = parseApiInstant(item.starts_at);
    // Logged meetings often carry no end, or one equal to the start.
    const stored = item.ends_at ? parseApiInstant(item.ends_at) : null;
    const end = stored && stored > start ? stored : new Date(start.getTime() + HOUR);
    setEditing(item);
    setTitle(item.title);
    setCompanyName(item.company_name);
    setCompanyId(item.company_id ?? "");
    setStarts(toInput(start, zone));
    setEnds(toInput(end, zone));
    setFormOpen(true);
    setError(null);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  async function saveItem() {
    const isEvent = !editing || editing.source === "event";
    if (isEvent && !companyId) {
      setError(reportSaveBlocked(show, "Choose a company for this event."));
      return;
    }
    if (!title.trim()) {
      setError(reportSaveBlocked(show, "Add a title."));
      return;
    }
    const startAt = fromInput(starts, zone);
    const endAt = fromInput(ends, zone);
    if (!startAt || !endAt || !(startAt < endAt)) {
      setError(reportSaveBlocked(show, "The event must end after it starts."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (!editing) {
        await opsApi.createEvent(orgId, {
          company_id: companyId,
          title: title.trim(),
          starts_at: startAt.toISOString(),
          ends_at: endAt.toISOString(),
          timezone: zone,
        });
      } else if (editing.source === "event") {
        await opsApi.updateEvent(orgId, editing.id, {
          company_id: companyId,
          title: title.trim(),
          starts_at: startAt.toISOString(),
          ends_at: endAt.toISOString(),
          timezone: zone,
        });
      } else {
        await crmApi.updateActivity(
          editing.id,
          {
            subject: title.trim(),
            company_id: companyId || null,
            occurred_at: startAt.toISOString(),
            ends_at: endAt.toISOString(),
          },
          orgId,
        );
      }
      closeForm();
      setCursor(dayOf(startAt, zone));
      reportSaved(show);
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, "The event could not be saved."));
    } finally {
      setBusy(false);
    }
  }

  async function removeItem(item: MeetingItem) {
    setError(null);
    try {
      if (item.source === "event") await opsApi.deleteEvent(orgId, item.id);
      else await crmApi.deleteActivity(item.id, orgId);
      if (editing?.id === item.id) closeForm();
      setSelected(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The item could not be deleted.");
    }
  }

  async function refreshCalendar() {
    setRefreshing(true);
    setError(null);
    try {
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The calendar could not be updated.");
    } finally {
      setRefreshing(false);
    }
  }

  async function prepare(item: MeetingItem) {
    setBusy(true);
    setError(null);
    try {
      const row = await mailboxApi.guidance(orgId, item.source, item.id);
      setSelected(row.meeting);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Guidance could not be prepared.");
    } finally {
      setBusy(false);
    }
  }

  const heading = schedule?.scope === "team" ? "Everyone's meetings" : schedule ? "Your meetings" : "Meetings";
  const formTitle = !editing ? "New event" : editing.source === "event" ? "Edit event" : "Edit meeting";

  return (
    <ListWorkspace
      title="Calendar"
      actions={
        <Cluster gap={2} align="center">
          {updatedAt ? (
            <p className="crm-muted sales-cal-updated" data-testid="sales-calendar-updated">
              Updated {formatWhen(updatedAt)}
            </p>
          ) : null}
          <Button size="sm" variant="secondary" type="button" disabled={refreshing} onClick={() => void refreshCalendar()}>
            {refreshing ? "Updating…" : "Update"}
          </Button>
          <Button size="sm" type="button" onClick={openCreate}>New event</Button>
          <Button size="sm" variant={view === "week" ? "primary" : "secondary"} onClick={() => setView("week")}>Week</Button>
          <Button size="sm" variant={view === "month" ? "primary" : "secondary"} onClick={() => setView("month")}>Month</Button>
          {view === "week" ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setCursor(addDays(weekStart, -7))}>Previous week</Button>
              <Button variant="secondary" size="sm" onClick={() => setCursor(null)}>This week</Button>
              <Button variant="secondary" size="sm" onClick={() => setCursor(addDays(weekStart, 7))}>Next week</Button>
            </>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={() => setCursor(addMonths(monthStart, -1))}>Previous month</Button>
              <Button variant="secondary" size="sm" onClick={() => setCursor(null)}>This month</Button>
              <Button variant="secondary" size="sm" onClick={() => setCursor(addMonths(monthStart, 1))}>Next month</Button>
            </>
          )}
        </Cluster>
      }
    >
      <p className="home-subtitle">
        {heading}. Each item shows the lead or opportunity stage it belongs to. Open one for how to run the conversation.
        {" "}Times are in {zone}, the organisation timezone.
        {schedule && !schedule.ai_ready ? " Connect AI in Settings if you want a talk track written for this meeting." : ""}
      </p>
      <p className="crm-muted" data-testid="sales-calendar-period">
        {view === "month"
          ? dayLabel(monthStart, { month: "long", year: "numeric" })
          : `${dayLabel(days[0], { day: "numeric", month: "short" })} – ${dayLabel(days[6], { day: "numeric", month: "short", year: "numeric" })}`}
      </p>
      {error ? <p role="alert" className="crm-error">{error}</p> : null}
      {formOpen ? (
        <Card>
          <Stack gap={3}>
            <h2 className="home-section-title">{formTitle}</h2>
            <Input label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
            <CompanyLookup
              orgId={orgId}
              value={companyName}
              companyId={companyId}
              onChange={({ name, companyId: nextId }) => {
                setCompanyName(name);
                setCompanyId(nextId);
              }}
            />
            <Cluster gap={3}>
              <Input label={`Starts (${zone})`} type="datetime-local" value={starts} onChange={(event) => setStarts(event.target.value)} />
              <Input label={`Ends (${zone})`} type="datetime-local" value={ends} onChange={(event) => setEnds(event.target.value)} />
            </Cluster>
            <Cluster gap={2}>
              <Button type="button" disabled={busy} onClick={() => void saveItem()}>
                {editing ? "Save changes" : "Save event"}
              </Button>
              <Button type="button" variant="secondary" onClick={closeForm}>Cancel</Button>
            </Cluster>
          </Stack>
        </Card>
      ) : null}
      <div className={view === "month" ? "sales-cal-month-scroll" : undefined}>
      {view === "month" ? (
        <div className="sales-cal-month sales-cal-weekdays" aria-hidden="true">
          {weekdays.map((day) => (
            <span key={day.toISOString()} className="sales-cal-weekday">
              {dayLabel(day, { weekday: "short" })}
            </span>
          ))}
        </div>
      ) : null}
      <div className={view === "month" ? "sales-cal-month" : "sales-cal-week"} data-testid="sales-calendar">
        {days.map((day) => {
          const items = meetings.filter((item) => dayOf(parseApiInstant(item.starts_at), zone).getTime() === day.getTime());
          const isToday = day.getTime() === today.getTime();
          const outside = view === "month" && day.getUTCMonth() !== monthStart.getUTCMonth();
          const dayClass = [
            "sales-cal-day",
            view === "month" ? "sales-cal-day--month" : "",
            outside ? "is-outside" : "",
            isToday ? "is-today" : "",
          ].filter(Boolean).join(" ");
          return (
            <section key={day.toISOString()} className={dayClass}>
              <h2 className="sales-cal-day-title">
                {view === "month"
                  ? dayLabel(day, { day: "numeric" })
                  : dayLabel(day, { weekday: "short", day: "numeric" })}
              </h2>
              {items.length === 0 && view === "week" ? <p className="crm-muted">No meetings</p> : null}
              {items.map((item) => (
                <button
                  key={`${item.source}-${item.id}`}
                  type="button"
                  className={eventClass(item.stage)}
                  onClick={() => setSelected(item)}
                >
                  <span className={stageClass(item.stage)}>{item.stage_label}</span>
                  <strong>{item.title}</strong>
                  <span className="crm-muted">{formatWhen(item.starts_at)}</span>
                  {item.company_name ? <span>{item.company_name}</span> : null}
                  {item.owner_id && item.owner_id !== session?.userId && item.owner_name ? (
                    <span className="crm-muted">{item.owner_name}</span>
                  ) : null}
                </button>
              ))}
            </section>
          );
        })}
      </div>
      </div>
      <div className="mailbox-layout sales-cal-panels">
        <Card padding="lg">
          <div className="sales-cal-panel">
          <Stack gap={3}>
            <h2 className="home-section-title">{view === "month" ? "Follow-ups due this month" : "Follow-ups due this week"}</h2>
            {(schedule?.follow_ups.length ?? 0) === 0 ? <p className="crm-muted">No follow-ups due.</p> : null}
            {schedule?.follow_ups.map((item) => (
              <button key={item.id} type="button" className={eventClass(item.stage)} onClick={() => setSelected(item)}>
                <span className={stageClass(item.stage)}>{item.stage_label}</span>
                <strong>{item.title}</strong>
                <span className="crm-muted">{formatWhen(item.starts_at)}{item.company_name ? ` · ${item.company_name}` : ""}</span>
              </button>
            ))}
          </Stack>
          </div>
        </Card>
        <Card padding="lg" data-testid="meeting-brief">
          <div className="sales-cal-panel">
          {selected ? (
            <Stack gap={3}>
              <h2 className="home-section-title">{selected.title}</h2>
              <p>{formatWhen(selected.starts_at)}{selected.company_name ? ` · ${selected.company_name}` : ""}</p>
              <p><span className={stageClass(selected.stage)}>{selected.stage_label}</span></p>
              {selected.stage_kind === "lead" ? (
                <p>Lead{selected.opportunity_title ? `: ${selected.opportunity_title}` : ""}</p>
              ) : selected.opportunity_title ? (
                <p>Deal: {selected.opportunity_title}</p>
              ) : (
                <p className="crm-muted">Not linked to an open lead or opportunity yet.</p>
              )}
              <Cluster gap={2}>
                {can(capabilityFor(selected), "update") ? (
                  <Button variant="secondary" disabled={busy} onClick={() => openEdit(selected)}>Edit</Button>
                ) : null}
                <DeleteRecordButton
                  capability={capabilityFor(selected)}
                  recordLabel={selected.title || "this item"}
                  onDeleted={() => removeItem(selected)}
                />
              </Cluster>
              <Stack gap={2}>
                <h3 className="home-section-title">How to run this</h3>
                <p>{selected.playbook}</p>
              </Stack>
              {selected.close_guidance ? (
                <Stack gap={2}>
                  <h3 className="home-section-title">Talk track</h3>
                  <p data-testid="meeting-talk-track">{selected.close_guidance}</p>
                </Stack>
              ) : null}
              <Button
                disabled={busy || schedule?.ai_ready === false}
                onClick={() => void prepare(selected)}
              >
                {busy ? "Preparing…" : "Prepare me for this meeting"}
              </Button>
              {schedule?.ai_ready === false ? (
                <p className="crm-muted">AI is not connected, so the stage advice above is the guide.</p>
              ) : (
                <p className="crm-muted">Prepare me writes a short talk track from the company, the stage, and this meeting.</p>
              )}
            </Stack>
          ) : (
            <p className="crm-muted">Select a meeting to see the stage and how to present it.</p>
          )}
          </div>
        </Card>
      </div>
    </ListWorkspace>
  );
}
